
import { useEffect, useRef, useState } from 'react';
import { 
  realtimeService, 
  RealtimeConfig, 
  RealtimeEventHandler 
} from '@/services/realtime/RealtimeService';

export interface UseUniversalRealtimeConfig {
  channelId: string;
  configs: RealtimeConfig[];
  enabled?: boolean;
}

export const useUniversalRealtime = (
  config: UseUniversalRealtimeConfig,
  handlers: RealtimeEventHandler
) => {
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [lastEventTime, setLastEventTime] = useState<Date | null>(null);
  
  const cleanupRef = useRef<(() => void) | null>(null);
  const handlersRef = useRef(handlers);
  const configRef = useRef(config);

  // Update refs when dependencies change
  useEffect(() => {
    handlersRef.current = handlers;
    configRef.current = config;
  }, [handlers, config]);

  useEffect(() => {
    // Fix: Use explicit boolean check instead of nullish coalescing
    if (config.enabled === false) {
      return;
    }

    console.log(`[useUniversalRealtime] Setting up subscription: ${config.channelId}`);

    // Enhanced handlers that track events
    const enhancedHandlers: RealtimeEventHandler = {
      onInsert: (payload) => {
        setLastEvent('INSERT');
        setLastEventTime(new Date());
        handlersRef.current.onInsert?.(payload);
      },
      onUpdate: (payload) => {
        setLastEvent('UPDATE');
        setLastEventTime(new Date());
        handlersRef.current.onUpdate?.(payload);
      },
      onDelete: (payload) => {
        setLastEvent('DELETE');
        setLastEventTime(new Date());
        handlersRef.current.onDelete?.(payload);
      },
      onError: (error) => {
        setConnectionStatus('error');
        handlersRef.current.onError?.(error);
      }
    };

    // Create subscription
    cleanupRef.current = realtimeService.subscribeToCustom(
      config.channelId,
      config.configs,
      enhancedHandlers
    );

    // Monitor connection status
    const statusInterval = setInterval(() => {
      const status = realtimeService.getConnectionStatus(config.channelId);
      setConnectionStatus(status);
    }, 1000);

    return () => {
      clearInterval(statusInterval);
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [config.channelId, config.enabled]);

  return {
    connectionStatus,
    lastEvent,
    lastEventTime,
    isConnected: connectionStatus === 'connected',
    isConnecting: connectionStatus === 'connecting',
    hasError: connectionStatus === 'error'
  };
};
